import { OnDestroy, ElementRef, Directive, Input } from '@angular/core';
import { RuntimeBridgeService, ComponentBridge } from '../_services/runtime-bridge.service';
import { GaugeSettings } from '../_models/hmi';

/**
 * 控件注册混入指令
 * 任何需要在运行时桥注册的控件都可以使用此指令
 *
 * 使用方法:
 * @Component({...})
 * export class MyGaugeComponent implements OnInit, OnDestroy {
 *   @Input() settings: GaugeSettings;
 *
 *   constructor(
 *     private elementRef: ElementRef,
 *     private runtimeBridge: RuntimeBridgeService
 *   ) {}
 *
 *   ngOnInit() {
 *     registerComponentToBridge(this, this.settings, this.elementRef, this.runtimeBridge);
 *   }
 *
 *   ngOnDestroy() {
 *     unregisterComponentFromBridge(this, this.settings, this.runtimeBridge);
 *   }
 *
 *   // 实现桥接接口
 *   getValue() { return this.value; }
 *   setValue(value: any) { this.value = value; }
 *   getProperties() { return this.settings.property; }
 *   setProperties(props: any) { Object.assign(this.settings.property, props); }
 *   getElement() { return this.elementRef.nativeElement; }
 * }
 */

/**
 * 注册组件到运行时桥
 * @param component 组件实例
 * @param settings 控件设置
 * @param elementRef 元素引用
 * @param runtimeBridge 运行时桥服务
 */
export function registerComponentToBridge(
    component: any,
    settings: GaugeSettings,
    elementRef: ElementRef,
    runtimeBridge: RuntimeBridgeService
): void {
    if (!settings?.scriptId) {
        return;
    }

    const bridge: ComponentBridge = {
        getValue: () => {
            if (typeof component.getValue === 'function') {
                return component.getValue();
            }
            // 默认返回 property.variableValue 或 value
            return component.value ?? component.settings?.property?.variableValue;
        },

        setValue: (value: any) => {
            if (typeof component.setValue === 'function') {
                component.setValue(value);
            } else if (component.value !== undefined) {
                component.value = value;
            }
        },

        getProperties: () => {
            if (typeof component.getProperties === 'function') {
                return component.getProperties();
            }
            return settings?.property || {};
        },

        setProperties: (properties: any) => {
            if (typeof component.setProperties === 'function') {
                component.setProperties(properties);
            } else if (settings?.property) {
                Object.assign(settings.property, properties);
            }
        },

        getElement: () => {
            if (typeof component.getElement === 'function') {
                return component.getElement();
            }
            return elementRef?.nativeElement || null;
        },

        callMethod: (methodName: string, ...args: any[]) => {
            if (typeof component[methodName] === 'function') {
                return component[methodName](...args);
            }
            console.warn(`[RuntimeBridge] 方法不存在: ${settings.scriptId}.${methodName}`);
            return undefined;
        }
    };

    runtimeBridge.registerComponent(settings.scriptId, bridge);
}

/**
 * 从运行时桥注销组件
 * @param component 组件实例
 * @param settings 控件设置
 * @param runtimeBridge 运行时桥服务
 */
export function unregisterComponentFromBridge(
    component: any,
    settings: GaugeSettings,
    runtimeBridge: RuntimeBridgeService
): void {
    if (settings?.scriptId) {
        runtimeBridge.unregisterComponent(settings.scriptId);
    }
}

/**
 * RuntimeBridge 混入指令
 * 自动处理组件的注册和注销
 */
@Directive({
    selector: '[runtimeBridge]'
})
export class RuntimeBridgeDirective implements OnDestroy {
    @Input() settings: GaugeSettings;

    constructor(
        private elementRef: ElementRef,
        private runtimeBridge: RuntimeBridgeService
    ) {}

    ngOnDestroy(): void {
        if (this.settings?.scriptId) {
            this.runtimeBridge.unregisterComponent(this.settings.scriptId);
        }
    }
}
